import { Role, DbRef, OfficeType } from '@caresend/types';
import { formatFullName, formatDateOfBirth, getOfficePath, initElementTimestamp, fetchWithRelationships } from '@caresend/utils';
import update from 'immutability-helper';
import '../../../database/algoliaConfig/indices.js';
import { needsNewKey } from '../../../database/algoliaConfig/methods.js';
import 'firebase/auth';
import { addUserToOfficeRequest, getAlgoliaAPIKeyForSearchOfficesRequest } from '../../../database/firebaseConfig/cloudFunctions.js';
import '../../../database/firebaseConfig/init.js';
import { dbSet, getValueOnce, getValuesOnceWithFilter } from '../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import '../../../router/helpers/initRouter.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { getUserRole, dispatchOrThrow } from '../../helpers.js';
import { initExtendedModule } from '../helpers.js';
import { loadOfficePatientsAction } from '../office/methods.js';

// Note: For logic that is exclusive to office staff, use the `office` module.
// For data and logic
const officesState = {
    offices: {},
    patientOptionsByOffice: {},
    officeSearchKeyData: null,
    officeSearchKeyForNameOnly: true,
};
const officesMutations = {
    'offices/ADD_OFFICE_PATIENT_OPTION': (state, { officeID, newOption }) => {
        var _a;
        /** Check if patient option already exists before adding */
        if ((_a = state.patientOptionsByOffice[officeID]) === null || _a === void 0 ? void 0 : _a.find((option) => option.id === newOption.id))
            return;
        if (!state.patientOptionsByOffice[officeID]) {
            state.patientOptionsByOffice = update(state.patientOptionsByOffice, {
                [officeID]: { $set: [newOption] },
            });
            return;
        }
        state.patientOptionsByOffice = update(state.patientOptionsByOffice, {
            [officeID]: { $unshift: [newOption] },
        });
    },
    'offices/RESET_OFFICES': (state) => {
        state.offices = {};
    },
    'offices/SET_OFFICE': (state, office) => {
        state.offices = {
            ...state.offices,
            [office.id]: office,
        };
    },
    'offices/SET_OFFICE_PATIENT_OPTIONS': (state, { officeID, patientOptions }) => {
        state.patientOptionsByOffice = update(state.patientOptionsByOffice, {
            [officeID]: { $set: patientOptions !== null && patientOptions !== void 0 ? patientOptions : [] },
        });
    },
    /** Used by setOrderDataInStore */
    'offices/SET_OFFICES': (state, offices) => {
        state.offices = {
            ...state.offices,
            ...offices,
        };
    },
    'offices/SET_OFFICES_SEARCH_KEY_DATA': (state, key) => {
        state.officeSearchKeyData = key;
    },
    'offices/SET_OFFICES_SEARCH_KEY_FOR_NAME_ONLY': (state, forNameOnly) => {
        state.officeSearchKeyForNameOnly = forNameOnly;
    },
    'offices/SET_UPDATED_PATIENT_OPTION': (state, { officeID, updatedPatient }) => {
        if (!updatedPatient.info)
            return;
        const officePatientOptions = state.patientOptionsByOffice[officeID];
        // Patient options for medical offices are not loaded outside of the order
        // flow, but a patient can be edited from the visit details page. Do nothing
        // if patient options are not loaded.
        if (!officePatientOptions)
            return;
        const optionIndex = officePatientOptions.findIndex((option) => option.id === updatedPatient.id);
        const patientOption = officePatientOptions[optionIndex];
        const name = formatFullName(updatedPatient);
        const dateOfBirth = updatedPatient.info.dateOfBirth
            ? formatDateOfBirth(updatedPatient.info.dateOfBirth)
            : 'Scheduling contact';
        const newOption = {
            ...patientOption,
            label: `${name} • ${dateOfBirth}`,
            info: {
                ...updatedPatient.info,
            },
            id: updatedPatient.id,
        };
        if (patientOption) {
            state.patientOptionsByOffice = update(state.patientOptionsByOffice, {
                [officeID]: { [optionIndex]: { $set: newOption } },
            });
        }
        else {
            state.patientOptionsByOffice = update(state.patientOptionsByOffice, {
                [officeID]: { $push: [newOption] },
            });
        }
    },
};
const officesActions = {
    'offices/addPatientToOffice': async ({ commit }, { officeID, patient }) => {
        /** TODO: Deprecate; is this needed? (?) */
        await addUserToOfficeRequest({ userID: patient.id, officeID });
        /** Set patient reference on office */
        const path = getOfficePath(officeID, `patients/${patient.id}`);
        await dbSet(path, initElementTimestamp(patient.id));
        const name = formatFullName(patient);
        const dateOfBirth = patient.info.dateOfBirth
            ? formatDateOfBirth(patient.info.dateOfBirth)
            : 'Scheduling contact';
        commit('offices/ADD_OFFICE_PATIENT_OPTION', {
            officeID,
            newOption: {
                id: patient.id,
                label: `${name} • ${dateOfBirth}`,
                info: patient.info,
            },
        });
        commit('users/SET_USER', patient);
    },
    'offices/getAlgoliaAPIKey': async ({ commit, state }, forOfficeNameSearchOnly = false) => {
        if (state.officeSearchKeyData
            && !needsNewKey(state.officeSearchKeyData)
            && state.officeSearchKeyForNameOnly === forOfficeNameSearchOnly)
            return state.officeSearchKeyData;
        const key = await getAlgoliaAPIKeyForSearchOfficesRequest({
            unauthenticatedOfficeNameSearch: forOfficeNameSearchOnly,
        });
        commit('offices/SET_OFFICES_SEARCH_KEY_DATA', key);
        commit('offices/SET_OFFICES_SEARCH_KEY_FOR_NAME_ONLY', forOfficeNameSearchOnly);
        return key;
    },
    'offices/fetchAllOfficesAsSuperAdmin': async ({ commit }) => {
        if (getUserRole() !== Role.SUPERADMIN)
            throw Error('Access denied');
        const offices = await getValueOnce(DbRef.OFFICES);
        if (!offices)
            throw Error('Failed to load offices');
        commit('offices/SET_OFFICES', offices);
    },
    'offices/fetchAllStaffingOffices': async ({ commit }) => {
        const offices = await getValuesOnceWithFilter(DbRef.OFFICES, 'type', OfficeType.STAFFING);
        if (!offices)
            throw Error('Failed to load offices');
        commit('offices/SET_OFFICES', offices);
    },
    'offices/fetchOffice': async ({ commit, dispatch }, { officeID, excludeDependencies = false }) => {
        const office = await getValueOnce(`${DbRef.OFFICES}/${officeID}`);
        if (!office)
            throw Error(`Failed to load office ${officeID}`);
        commit('offices/SET_OFFICE', office);
        if (!excludeDependencies)
            await dispatch('offices/fetchOfficeDependencies', { officeID });
    },
    // Note: This is also called by the office module to fetch dependencies
    // for office staff when they log in. Not supported in patient app.
    async 'offices/fetchOfficeDependencies'({ commit, rootState, state, dispatch, rootGetters }, { officeID }) {
        var _a, _b;
        const office = state.offices[officeID];
        if (!office)
            throw Error(`Missing office ${officeID}`);
        const defaultFetchWithRelationshipsCallbacks = rootGetters['app/getDefaultFetchWithRelationshipsCallbacks'](commit);
        const relationships = {
            assistants: true,
            prescribers: true,
        };
        const { users } = await fetchWithRelationships(DbRef.OFFICES, officeID, relationships, { writers: defaultFetchWithRelationshipsCallbacks });
        commit('users/SET_STAFF_USERS', {
            staffUsers: Object.values(users !== null && users !== void 0 ? users : {}),
            extension: { officeID },
        });
        const { sharedPlaceAccounts } = office;
        if (sharedPlaceAccounts) {
            const placeAccountIDs = Object.keys(sharedPlaceAccounts);
            dispatchOrThrow(this, 'places/fetchPlaceAccountsAndGroups', {
                placeAccountIDs,
                placeGroupID: (_b = (_a = rootState.office) === null || _a === void 0 ? void 0 : _a.office) === null || _b === void 0 ? void 0 : _b.placeGroupID,
            });
        }
        await dispatch('offices/loadOfficePatients', { officeID });
    },
    'offices/loadOfficePatients': loadOfficePatientsAction,
};
const officesGetters = {
    'offices/getOfficePatientOption': (state) => (officeID, patientID) => { var _a; return (_a = state.patientOptionsByOffice[officeID]) === null || _a === void 0 ? void 0 : _a.find((option) => option.id === patientID); },
    'offices/isPatientInOptions': (state) => (officeID, patientID) => !!officesGetters['offices/getOfficePatientOption'](state)(officeID, patientID),
};
const officesModule = {
    state: officesState,
    mutations: officesMutations,
    actions: officesActions,
    getters: officesGetters,
};
const initOfficesModule = (moduleExtensions) => initExtendedModule(officesModule, moduleExtensions);

export { initOfficesModule };
